import { useCallback, useEffect, useRef } from 'react';
import { getAppConfig, isDeveloperMode } from '../utils/config';

export interface VersionTriggerOptions {
  onTrigger?: () => void;
  enabled?: boolean;
  shortcut?: string;
  mobileGestureEnabled?: boolean;
}

export interface VersionTriggerState {
  isEnabled: boolean;
  shortcut: string;
  mobileGestureEnabled: boolean;
  lastTriggerTime: number;
}

export function useVersionTrigger(options: VersionTriggerOptions = {}): VersionTriggerState {
  const {
    onTrigger,
    enabled = isDeveloperMode(),
    shortcut = getAppConfig().versionPopupShortcut,
    mobileGestureEnabled = getAppConfig().mobileGestureEnabled
  } = options;

  const lastTriggerTime = useRef(0);
  const touchCount = useRef(0);
  const touchTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const touchStartTime = useRef(0);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger when typing in input fields
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
      return;
    }

    // Check for Shift+V shortcut
    if (event.shiftKey && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      triggerVersionPopup();
    }
  }, []);

  // Handle mobile gestures
  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (!mobileGestureEnabled) return;

    const touches = event.touches;
    
    // 3-finger long press
    if (touches.length === 3) {
      touchStartTime.current = Date.now();
      longPressTimer.current = setTimeout(() => {
        triggerVersionPopup();
      }, 1000); // 1 second long press
    }
    
    // Triple tap detection
    touchCount.current++;
    
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
    }
    
    touchTimer.current = setTimeout(() => {
      if (touchCount.current >= 3) {
        triggerVersionPopup();
      }
      touchCount.current = 0;
    }, 500); // 500ms window for triple tap
  }, [mobileGestureEnabled]);

  const handleTouchEnd = useCallback(() => {
    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    // Cancel long press if finger moves
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Trigger version popup
  const triggerVersionPopup = useCallback(() => {
    const now = Date.now();
    
    // Prevent multiple triggers within 1 second
    if (now - lastTriggerTime.current < 1000) {
      return;
    }
    
    lastTriggerTime.current = now;
    onTrigger?.();
  }, [onTrigger]);

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return;

    // Keyboard events
    document.addEventListener('keydown', handleKeyDown);

    // Touch events for mobile gestures
    if (mobileGestureEnabled) {
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      
      if (mobileGestureEnabled) {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchmove', handleTouchMove);
      }

      // Clean up timers
      if (touchTimer.current) {
        clearTimeout(touchTimer.current);
      }
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, [enabled, mobileGestureEnabled, handleKeyDown, handleTouchStart, handleTouchEnd, handleTouchMove]);

  return {
    isEnabled: enabled,
    shortcut,
    mobileGestureEnabled,
    lastTriggerTime: lastTriggerTime.current
  };
}

/**
 * Check if the current environment supports version triggers
 */
export function isVersionTriggerSupported(): boolean {
  return isDeveloperMode() || getAppConfig().mobileGestureEnabled;
}

/**
 * Get available trigger methods
 */
export function getAvailableTriggers(): string[] {
  const triggers: string[] = [];
  
  if (isDeveloperMode()) {
    triggers.push('Keyboard: Shift+V');
  }
  
  if (getAppConfig().mobileGestureEnabled) {
    triggers.push('Mobile: 3-finger long press');
    triggers.push('Mobile: Triple tap');
  }
  
  return triggers;
} 